import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { type DateRange, LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import { makeStyles } from "@mui/styles";
import {
  DataGridPro,
  GridColumnMenuContainer,
  type GridColumnMenuProps,
  type GridColumnVisibilityModel,
  type GridColumns,
  GridCsvExportMenuItem,
  type GridEventListener,
  GridFilterMenuItem,
  type GridFilterModel,
  GridFooter,
  GridOverlay,
  type GridRowsProp,
  type GridSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  SortGridMenuItems,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import type { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import { Methods } from "api";
import { MINIMUM_NUMBER_OF_CHARACTERS_TO_SEARCH } from "common/constants/common";
import * as MESSAGES from "common/constants/messages";
import * as constants from "common/constants/theme";
import {
  type ActionButtonId,
  type ActionButtonList,
  ActionButtonNames,
  type CSVData,
  type CSVHeader,
  type ColumnData,
  type FilterData,
  type TypeClaim,
} from "common/types/commonTypes";
import type { LOADING_STATES } from "common/types/loading";
import { ConfirmationDialog } from "components/confirmation";
import DateRangeCalendar from "components/dateRangeCalendar/DateRangeCalendar";
import type { Dayjs } from "dayjs";
import { useAPI, usePopups } from "hooks";
import type { ReactElement } from "react";
import * as React from "react";
import { useDebounce } from "use-debounce";
import { arrayOfFilterYears } from "utils/helpers";
import type { UpdateOrgUserSettingsInput, UpdateOrgUserSettingsResponse } from "./types";
import { useProDataGrid } from "./useProDataGrid";

const useStyles = makeStyles((theme) => ({
  errorText: {
    marginTop: ".5rem",
    marginLeft: "1rem",
    color: theme.palette.warning.dark,
    fontWeight: "bold",
  },
}));

interface ProDataGridProps {
  title: string;
  subtitle?: string;
  rows: GridRowsProp;
  columns: GridColumns;
  ccgList: ColumnData[];
  assigneeList?: ColumnData[];
  initialState?: GridInitialStatePro;
  loading?: boolean;
  ccgLoading?: boolean;
  assigneeListLoading?: boolean;
  disableSelectionOnClick?: boolean;
  checkboxSelection?: boolean;
  rowsPerPageOptions?: number[];
  onCellEditCommit?: GridEventListener<"cellEditCommit">;
  onClickRowData?: (val: TypeClaim) => void;
  debouncedSearchString?: (val: FilterData) => void;
  required?: boolean;
  requiredAssigneeList?: boolean;
  helperText?: string;
  placeholder?: string;
  dataError?: boolean;
  autoFocus?: boolean;
  errorText?: string;
  filterData: FilterData | null;
  ccgValue?: (val: ColumnData | null) => void;
  yearValue?: (val: ColumnData | null) => void;
  assigneeValue?: (val: ColumnData | null) => void;
  filterCcgData?: ColumnData | null;
  filterYearData?: ColumnData | null;
  filterAssigneeData?: ColumnData | null;
  dateRange?: (val: DateRange<Dayjs>) => void;
  csvData?: [CSVHeader[], CSVData[]];
  selectedCsvData?: [CSVHeader[], CSVData[]];
  button?: ActionButtonList;
  buttonId?: (val: ActionButtonId) => void;
  selectedRows?: (val: string[]) => void;
  refetchData?: () => void;
  dateRangeLoading?: boolean;
  isAssigneeList?: boolean;
  endpointsLoading?: LOADING_STATES | null;
}

const ProDataGrid: React.FC<ProDataGridProps> = ({
  title,
  subtitle,
  rows,
  columns,
  isAssigneeList = false,
  assigneeList,
  ccgList,
  loading,
  ccgLoading,
  assigneeListLoading,
  debouncedSearchString,
  required = false,
  requiredAssigneeList = false,
  helperText,
  placeholder,
  dataError = false,
  autoFocus,
  errorText = "Please select item before search",
  filterData,
  ccgValue,
  yearValue,
  assigneeValue,
  filterCcgData,
  filterYearData,
  filterAssigneeData,
  dateRange,
  csvData,
  selectedCsvData = [[], []],
  button,
  buttonId,
  selectedRows,
  refetchData,
  initialState,
  onClickRowData,
  onCellEditCommit,
  disableSelectionOnClick = true,
  checkboxSelection = true,
  rowsPerPageOptions = [50, 100, 250],
  dateRangeLoading = false,
  endpointsLoading,
  ...props
}) => {
  const classes = useStyles();
  const apiRef = useGridApiRef();
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(rowsPerPageOptions[0]);
  const [openCalendar, setOpenCalendar] = React.useState<boolean>(false);
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const [searchString, setSearchString] = React.useState<string>(filterData ? filterData.searchString : "");
  const [ccgKey, setCcgKey] = React.useState<ColumnData | null>(filterCcgData ?? null);
  const [yearKey, setYearKey] = React.useState<ColumnData | null>(filterYearData ?? null);
  const [assigneeKey, setAssigneeKey] = React.useState<ColumnData | null>(filterAssigneeData ?? null);
  const { handleBannerMessage } = usePopups();

  const [searchFocus, setSearchFocus] = React.useState<boolean>(false);
  const [errorSearch, setErrorSearch] = React.useState<boolean>(false);
  const [debouncedString] = useDebounce(searchString.trim(), 1000);

  const shouldGetCcgListRef = React.useRef<boolean>(true);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [],
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>({
    id: false,
    __check__: true,
  });

  // Sort the filter lists
  assigneeList = assigneeList?.sort((a, b) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
  ccgList = ccgList?.sort((a, b) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));

  const { isSelected, setIsSelected, selectionModel, setSelectionModel, allSelectedRows, setAllSelectedRows } =
    useProDataGrid();
  const [currentPage, setCurrentPage] = React.useState<number>(0);

  useAPI<UpdateOrgUserSettingsResponse, undefined>({
    method: Methods.GET,
    fieldName: "getOrgUserSettings",
    manual: false,
    onCompleted(data) {
      if (!data?.settings) {
        return;
      }
      setColumnVisibilityModel({
        ...data?.settings,
        id: data?.settings?.id ?? false,
      });
    },
  });

  // loading status
  const { trigger: updateOrgUserSettings } = useAPI<
    UpdateOrgUserSettingsResponse,
    {
      input: UpdateOrgUserSettingsInput;
    }
  >({
    method: Methods.PUT,
    fieldName: "updateOrgUserSettings",
  });

  /**
   * Fetches the currently visible rows after filtering/sorting is applied.
   */
  const getVisibleRows = () => {
    return apiRef.current.getVisibleRowModels();
  };

  /**
   * Handles the selection of rows for the currently visible rows on the current page.
   * - Updates the cumulative selection across all pages.
   * - Retains previous selections while adding new selections for the current page.
   * - Passes the updated selection back to the parent component.
   *
   * @param newSelection - The newly selected rows (from the current page).
   */
  const handleSelectionChange = (newSelection: GridSelectionModel) => {
    const visibleRowsOnPage = [...getVisibleRows().values()].slice(
      currentPage * currentPageSize,
      (currentPage + 1) * currentPageSize,
    );

    const visibleSelection = newSelection.filter((id) => visibleRowsOnPage.some((row) => row.id === id));

    const updatedSelection = [
      ...allSelectedRows.filter((id) => !visibleRowsOnPage.some((row) => row.id === id)),
      ...visibleSelection,
    ];

    setAllSelectedRows(updatedSelection);
    setSelectionModel(updatedSelection);
    selectedRows?.(updatedSelection.map((id) => id.toString()));
    setIsSelected(updatedSelection.length > 0);
  };

  /**
   * Handles the page change event.
   * - Updates the current page and adjusts the selection model for visible rows.
   *
   * @param newPage - The index of the new page.
   */
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    const visibleRowsOnPage = [...getVisibleRows().values()].slice(
      newPage * currentPageSize,
      (newPage + 1) * currentPageSize,
    );
    const visibleSelectionOnPage = allSelectedRows.filter((id) => visibleRowsOnPage.some((row) => row.id === id));
    setSelectionModel(visibleSelectionOnPage);
  };

  /**
   * Handles the change in page size, updating the page size and resetting the current page.
   * - Adjusts the selection model to display rows only visible with the new page size.
   *
   * @param newPageSize - The new number of rows per page.
   */
  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageSize(newPageSize);
    setCurrentPage(0);
    const visibleRowsOnPage = [...getVisibleRows().values()].slice(0, newPageSize);
    const visibleSelectionOnPage = allSelectedRows.filter((id) => visibleRowsOnPage.some((row) => row.id === id));
    setSelectionModel(visibleSelectionOnPage);
  };

  const handleFilterChange = (model: GridFilterModel) => {
    setFilterModel(model);
  };

  const isFilterApplied = React.useMemo(() => filterModel.items.length > 0, [filterModel.items]);
  const isAllColumnsHidden = React.useMemo(() => {
    if (Object.keys(columnVisibilityModel).length === 0) {
      return false;
    }
    // Iterate through all possible columns defined in `allColumns`
    for (const column of columns) {
      const fieldName = column.field;
      if (columnVisibilityModel[fieldName] !== false) {
        return false;
      }
    }
    return true;
  }, [columnVisibilityModel, columns]);

  // Handle Search
  React.useEffect(() => {
    if (debouncedString.length > MINIMUM_NUMBER_OF_CHARACTERS_TO_SEARCH) {
      debouncedSearchString?.({ searchString: debouncedString });
    } else if (debouncedString.length === 0) {
      refetchData?.();
    }
  }, [debouncedString, debouncedSearchString]);

  // update the CCG filter list
  React.useEffect(() => {
    if (!shouldGetCcgListRef.current) return;
    if (ccgList && ccgList?.length === 1) {
      ccgValue?.(ccgList?.[0]);
      setCcgKey(ccgList?.[0]);
      shouldGetCcgListRef.current = false;
    }
  }, [ccgList]);

  const unhideAllColumns = () => {
    const model = {
      id: true,
      displayId: true,
      doctorName: true,
      ccg: true,
      receivedDate: true,
      visitTime: true,
      assessmentTookPlace: true,
      assessmentPostcode: true,
      defaultMHT: true,
      employer: true,
      postcode: true,
      mileage: true,
      approverName: true,
      approver: true,
      payer: true,
      lineManager: true,
      amhpTeamName: true,
      doctorAdditionalConfCol: true,
      expenses: true,
      doctorEmployedStatusCol: true,
      approvedDate: true,
      visitDate: true,
      paidDate: true,
      __check__: true,
    };
    setColumnVisibilityModel(model);
    updateOrgUserSettings({
      input: {
        settings: model,
      },
    });
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <Grid
          container
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          mb={3}>
          <Grid item mb={constants.ButtonSpacing}>
            <Typography variant="h1" data-test-id={`title-${title.replaceAll(" ", "-").toLowerCase()}`}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="h4" color="textSecondary" sx={{ mt: 2, fontWeight: "light" }}>
                {subtitle}
              </Typography>
            )}
          </Grid>

          <Grid item>
            <Grid container spacing={constants.ButtonSpacing}>
              <Grid item>
                <GridToolbarExportContainer variant="contained" color="primary" data-testid="claim-export-options">
                  {isSelected ? (
                    <>
                      <MenuItem
                        onClick={() => {
                          setOpenDialog(true);
                        }}
                        data-testid="claim-export-selected-by-csv">
                        Export Selected
                      </MenuItem>
                    </>
                  ) : (
                    <>
                      <GridCsvExportMenuItem data-testid="claim-export-selected-by-table" />
                    </>
                  )}
                  <MenuItem
                    onClick={() => {
                      setOpenCalendar(true);
                    }}
                    data-testid="claim-export-selected-by-range">
                    Export By Range
                  </MenuItem>
                </GridToolbarExportContainer>
              </Grid>

              {button?.markPaid && (
                <Grid item>
                  <LoadingButton
                    disabled={!isSelected || endpointsLoading !== null || button.assignClaims?.loading}
                    loading={button.markPaid.loading}
                    variant="contained"
                    color="secondaryWhite"
                    startIcon={button.markPaid.icon}
                    onClick={() => {
                      button?.markPaid && buttonId && buttonId(button.markPaid.id);
                    }}
                    data-testid="claim-mark-as-paid">
                    {ActionButtonNames.PAID}
                  </LoadingButton>
                </Grid>
              )}

              {button?.approve && (
                <Grid item>
                  <LoadingButton
                    disabled={!isSelected || endpointsLoading !== null || button.assignClaims?.loading}
                    loading={button.approve.loading}
                    variant="contained"
                    color="secondary"
                    startIcon={button.approve.icon}
                    onClick={() => {
                      button?.approve && buttonId && buttonId(button.approve.id);
                    }}
                    data-testid="claim-approve">
                    {ActionButtonNames.APPROVE}
                  </LoadingButton>
                </Grid>
              )}

              {button?.putOnHoldClaims && (
                <Grid item>
                  <LoadingButton
                    disabled={!isSelected || endpointsLoading !== null || button.assignClaims?.loading}
                    loading={button.putOnHoldClaims.loading}
                    variant="contained"
                    color="warning"
                    startIcon={button.putOnHoldClaims.icon}
                    onClick={() => {
                      button?.putOnHoldClaims && buttonId && buttonId(button.putOnHoldClaims.id);
                    }}
                    data-testid="claim-on-hold">
                    {ActionButtonNames.ON_HOLD}
                  </LoadingButton>
                </Grid>
              )}

              {button?.assignMe && (
                <Grid item>
                  <LoadingButton
                    disabled={!isSelected || endpointsLoading !== null || button.assignClaims?.loading}
                    loading={button.assignMe.loading}
                    variant="contained"
                    color="primary"
                    startIcon={button.assignMe.icon}
                    onClick={() => {
                      button?.assignMe && buttonId && buttonId(button.assignMe.id);
                    }}
                    data-testid="claim-assign-to-me">
                    {ActionButtonNames.ASSIGN_TO_ME}
                  </LoadingButton>
                </Grid>
              )}

              {button?.assignClaims && (
                <Grid item>
                  <LoadingButton
                    disabled={!isSelected || endpointsLoading !== null}
                    loading={button.assignClaims.loading}
                    variant="contained"
                    color="primary"
                    startIcon={button.assignClaims.icon}
                    onClick={() => {
                      button?.assignClaims && buttonId && buttonId(button.assignClaims.id);
                    }}
                    data-testid="claim-assign-to-another-user">
                    {ActionButtonNames.ASSIGN_TO_ANOTHER}
                  </LoadingButton>
                </Grid>
              )}

              {button?.unassignClaims && (
                <Grid item>
                  <LoadingButton
                    disabled={!isSelected || endpointsLoading !== null || button.assignClaims?.loading}
                    loading={button.unassignClaims.loading}
                    variant="contained"
                    color="primary"
                    startIcon={button.unassignClaims.icon}
                    onClick={() => {
                      button?.unassignClaims && buttonId && buttonId(button.unassignClaims.id);
                    }}
                    data-testid="claim-unassign">
                    {ActionButtonNames.UNASSIGN}
                  </LoadingButton>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
          spacing={constants.ButtonSpacing}>
          {/* FILTER - SEARCH */}
          <Grid item>
            <Stack sx={{ display: "flex", flexDirection: "column" }}>
              <TextField
                sx={{
                  marginTop: 0.35,
                  minWidth: 150,
                }}
                size="small"
                variant="standard"
                placeholder="Search..."
                value={searchString}
                autoFocus={searchFocus}
                id="claims-user-input"
                data-testid="claims-test-user-input"
                onChange={(e) => {
                  setSearchString(e.target.value);
                  setSearchFocus(true);
                  if (e.target.value.length <= MINIMUM_NUMBER_OF_CHARACTERS_TO_SEARCH && e.target.value.length > 0) {
                    setErrorSearch(true);
                  } else {
                    setErrorSearch(false);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchString.length > 0 && (
                        <IconButton
                          data-testid="claim-user-input-clear"
                          onClick={() => {
                            setSearchString("");
                            refetchData?.();
                            setErrorSearch(false);
                          }}>
                          <CloseIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {errorSearch && (
                <div className={classes.errorText}>
                  <Typography variant="h5">Please type least three characters</Typography>
                </div>
              )}
            </Stack>
          </Grid>

          {/* FILTER - YEAR */}
          <Grid item>
            <Autocomplete
              id="filter_year"
              options={arrayOfFilterYears()}
              getOptionLabel={(option) => option.value.toString()}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              loading={false}
              sx={{ minWidth: 150 }}
              value={yearKey ?? undefined}
              onChange={(_event, value) => {
                setSearchFocus(false);
                if (value) {
                  yearValue?.(value);
                  setYearKey(value);
                } else {
                  yearValue?.(null);
                  setYearKey(null);
                }
              }}
              renderInput={(params) => (
                <>
                  <Stack sx={{ display: "flex", flexDirection: "row" }}>
                    <FilterListIcon sx={{ marginRight: -3, marginTop: 0.5 }} />
                    <TextField
                      sx={{
                        ".MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                          marginLeft: 4,
                        },
                      }}
                      {...params}
                      variant="standard"
                      placeholder="Year"
                      required={required}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      data-testid="claim-year-filter-input"
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  </Stack>
                </>
              )}
            />
          </Grid>

          {/* FILTER - CCG/ICB */}
          <Grid item>
            <Autocomplete
              id="filter_ccg"
              options={ccgList}
              getOptionLabel={(option) => option.value}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              loading={ccgLoading}
              // Disable the CCG filter field if the user only have one CCG to avoid unnecessary issues
              disabled={ccgList && ccgList?.length === 1}
              sx={{ minWidth: 200 }}
              value={ccgKey ?? undefined}
              onChange={(_event, value) => {
                setSearchFocus(false);
                if (value) {
                  ccgValue?.(value);
                  setCcgKey(value);
                } else {
                  ccgValue?.(null);
                  setCcgKey(null);
                }
              }}
              renderInput={(params) => (
                <>
                  <Stack sx={{ display: "flex", flexDirection: "row" }}>
                    <FilterListIcon sx={{ marginRight: -3, marginTop: 0.5 }} />
                    <TextField
                      sx={{
                        ".MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                          marginLeft: 4,
                        },
                      }}
                      {...params}
                      variant="standard"
                      placeholder="CCG Filter"
                      required={required}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      data-testid="claim-ccg-filter-input"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {ccgLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  </Stack>
                </>
              )}
            />
          </Grid>

          {/* FILTER - CLAIM ASSIGNEE */}
          {isAssigneeList && (
            <Grid item>
              <Autocomplete
                id="filter_assignee"
                options={assigneeList ?? []}
                getOptionLabel={(option) => option.value}
                isOptionEqualToValue={(option, value) => option.key === value.key}
                loading={assigneeListLoading}
                sx={{ minWidth: 200 }}
                value={assigneeKey}
                onChange={(_event, value) => {
                  setSearchFocus(false);
                  if (value) {
                    assigneeValue?.(value);
                    setAssigneeKey(value);
                  } else {
                    assigneeValue?.(null);
                    setAssigneeKey(null);
                  }
                }}
                renderInput={(params) => (
                  <>
                    <Stack sx={{ display: "flex", flexDirection: "row" }}>
                      <FilterListIcon sx={{ marginRight: -3, marginTop: 0.5 }} />
                      <TextField
                        sx={{
                          ".MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                            marginLeft: 4,
                          },
                        }}
                        {...params}
                        variant="standard"
                        placeholder="Assignee Filter"
                        required={required}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        data-testid="claim-assignee-filter-input"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {assigneeListLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    </Stack>
                  </>
                )}
              />
            </Grid>
          )}
          {isFilterApplied && (
            <Grid item>
              <Button
                data-testid="claim-filter-clear"
                variant="contained"
                color="primary"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setFilterModel({ items: [] });
                }}>
                Clear applied table filter
              </Button>
            </Grid>
          )}
          {isAllColumnsHidden && (
            <Grid item>
              <Button
                data-testid="claim-all-columns-hidden"
                variant="contained"
                color="primary"
                onClick={unhideAllColumns}>
                Restore all hidden columns
              </Button>
            </Grid>
          )}
        </Grid>
      </GridToolbarContainer>
    );
  };

  const CustomFooter = () => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Grid item>
          <GridToolbarColumnsButton data-testid="claim-toolbar-columns" />
          <GridToolbarDensitySelector data-testid="claim-toolbar-density" />
        </Grid>
        <Grid item>
          <GridFooter />
        </Grid>
      </GridToolbarContainer>
    );
  };

  const CustomColumnMenu = (props: GridColumnMenuProps): ReactElement<any, any> | null => {
    const { hideMenu, currentColumn } = props;
    return (
      <GridColumnMenuContainer hideMenu={hideMenu} currentColumn={currentColumn} open={false}>
        <SortGridMenuItems onClick={hideMenu} column={currentColumn} />
        <GridFilterMenuItem onClick={hideMenu} column={currentColumn} />
      </GridColumnMenuContainer>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <DateRangeCalendar
        open={openCalendar}
        closeDateRange={() => {
          setOpenCalendar(false);
        }}
        csvData={csvData}
        dateRange={(val) => {
          dateRange?.(val);
        }}
        loading={dateRangeLoading}
        successCallback={() => {
          setOpenCalendar(false);
          handleBannerMessage("success", "Claims exported successfully completed");
        }}
      />

      <ConfirmationDialog
        open={openDialog}
        selectedCsvData={selectedCsvData}
        message={MESSAGES.CONFIRM_EXPORT_CLAIMS.replace(
          "{count}",
          `${
            selectedCsvData?.[1]?.length === 1
              ? `${selectedCsvData[1].length} claim`
              : `${selectedCsvData[1].length} claims`
          }`,
        )}
        closeConfirmation={() => {
          setOpenDialog(false);
        }}
        successCallback={() => {
          handleBannerMessage("success", "Claims exported successfully completed");
        }}
      />
      <DataGridPro
        {...props}
        apiRef={apiRef}
        localeText={{
          toolbarExport: "Export Options",
          toolbarExportCSV: "Export Table",
        }}
        components={{
          Toolbar: CustomToolbar,
          Footer: CustomFooter,
          ColumnMenu: CustomColumnMenu,

          NoRowsOverlay: () => (
            <GridOverlay>
              <Typography>{MESSAGES.NO_MATCHING_RECORDS}</Typography>
            </GridOverlay>
          ),
        }}
        onRowClick={(event) => {
          onClickRowData?.(event.row as TypeClaim);
        }}
        filterModel={filterModel}
        onFilterModelChange={handleFilterChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model) => {
          // This condition is to handle `show all` button trigger as the model object is always an empty object
          if (Object.keys(model).length === 0) {
            unhideAllColumns();
          } else {
            setColumnVisibilityModel(model);
            updateOrgUserSettings({ input: { settings: model } });
          }
        }}
        disableColumnPinning={true}
        disableColumnReorder={true}
        disableSelectionOnClick={disableSelectionOnClick}
        checkboxSelection={checkboxSelection}
        selectionModel={selectionModel}
        onSelectionModelChange={handleSelectionChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onCellEditCommit={onCellEditCommit}
        columns={columns}
        rows={rows}
        error={dataError || undefined}
        loading={loading}
        pageSize={currentPageSize}
        rowsPerPageOptions={rowsPerPageOptions}
        initialState={initialState}
        pagination
        autoHeight={true}
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
          "& .MuiDataGrid-row": {
            ":hover": {
              cursor: "pointer",
              backgroundColor: constants.lightBaseGrey,
            },
          },
          "& .super-app-theme--even": {
            backgroundColor: constants.lightGrey,
          },
          "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none",
          },
        }}
        getRowClassName={(params) => `super-app-theme--${params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"}`}
      />
    </Box>
  );
};

export default React.memo(ProDataGrid);
